import React from 'react';
import { CurationHeroHeaderCuration } from '../../CurationHeroHeader.types';
import { StyledBlockText, StyledButtonsContainer, StyledContainer, StyledContent, StyledEyebrow, StyledHed, StyledSubtitle, styles } from './Billboard.styles';
import { Box, Flex } from '@withjoy/joykit';
import { CurationVideoPlayer } from '../../../../../../components/CurationVideoPlayer/CurationVideoPlayer';
import { CurationCta } from '../CurationCta/CurationCta';
import { SkeletonText } from '@shared/components/Skeleton';
import { CustomCta } from '../CustomCta/CustomCta';
import { CSSObject } from '@withjoy/joykit/components/Box/Box.types';

export type BillboardColorMode = 'light' | 'dark';
export type OverviewBillboard = Readonly<{
  customButton?: string;
  disableBackgroundGradient?: boolean;
  colorMode?: BillboardColorMode;
}>;

export type BillboardVariants = 'default' | 'overviewSlider' | 'overviewCard';
export interface BillboardProps
  extends OverviewBillboard,
    Readonly<{
      curation: CurationHeroHeaderCuration;
      handleOnViewCurationClick: React.MouseEventHandler;
      onOpenVideoPlayer?: (curationId: string) => void;
      onCloseVideoPlayer?: () => void;
      isMobile?: boolean;
      isSliderItem?: boolean;
      showDescription?: boolean;
      alignment?: 'left' | 'center' | 'right';
      variant?: BillboardVariants;
      contentStyles?: CSSObject;
      buttonStyles?: CSSObject;
    }> {}

const Billboard = React.forwardRef<HTMLDivElement, BillboardProps>((props: BillboardProps, ref) => {
  const { eyebrow, title, description } = props.curation.header;
  const { videoUrl } = props.curation;
  const {
    curation,
    isMobile,
    isSliderItem,
    showDescription = false,
    handleOnViewCurationClick,
    onOpenVideoPlayer,
    onCloseVideoPlayer,
    alignment = 'center',
    customButton,
    colorMode = 'light',
    variant = 'default',
  } = props;

  const alignmentType = isMobile ? 'center' : alignment;

  return (
    <StyledContainer
      __css={styles.container(variant, alignmentType, isSliderItem)}
      ref={ref}
    >
      <StyledContent __css={{ ...styles.content(colorMode, alignmentType), ...props.contentStyles }}>
        <StyledBlockText __css={styles.blockText(alignmentType, variant)}>
          {eyebrow && <StyledEyebrow __css={isSliderItem ? styles.eyebrowSlider(variant) : styles.eyebrow(variant)}>{eyebrow}</StyledEyebrow>}
          {title && (
            <StyledHed
              as="h1"
              __css={isSliderItem ? styles.hedSlider(variant) : styles.hed(variant)}
            >
              {title}
            </StyledHed>
          )}
          {description && showDescription && (
            <StyledSubtitle
              className="billboard-description"
              {...styles.subtitleSlider}
            >
              {description}
            </StyledSubtitle>
          )}
        </StyledBlockText>
        <StyledButtonsContainer __css={styles.buttonsContainer(variant, colorMode, props.buttonStyles)}>
          <Flex
            flexDirection={['column-reverse', null, 'row']}
            alignItems="center"
            justifyContent={alignmentType === 'left' ? 'flex-start' : alignmentType == 'right' ? 'flex-end' : 'center'}
          >
            {!customButton && (
              <CurationCta
                method="curationsHeroSection"
                onClick={handleOnViewCurationClick}
                borderColor={[videoUrl ? 'transparent' : 'white', null, 'white']}
              />
            )}
            {customButton && (
              <CustomCta
                method="curationsHeroSection"
                onClick={handleOnViewCurationClick}
                buttonText={customButton}
              />
            )}
            {videoUrl && (
              <CurationVideoPlayer
                curationId={curation.id}
                variant="default"
                videoUrl={videoUrl}
                eyebrow={eyebrow}
                title={title}
                handleOnViewCurationClick={handleOnViewCurationClick}
                onOpenVideoPlayer={() => onOpenVideoPlayer?.(curation.id)}
                onCloseVideoPlayer={onCloseVideoPlayer}
              />
            )}
          </Flex>
        </StyledButtonsContainer>
      </StyledContent>
    </StyledContainer>
  );
});

const BillboardSkeleton = () => {
  return (
    <Box>
      <StyledContainer __css={styles.container('default')}>
        <StyledContent __css={styles.content('light')}>
          <StyledBlockText __css={styles.blockText()}>
            <SkeletonText
              rows={1}
              skeletonHeight={['30px', null, '35px']}
              width={['130px', null, '190px']}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={['120px', '60px', '85px']}
              width={['220px', '350px', '650px']}
              margin="15px"
            />
          </StyledBlockText>
          <StyledButtonsContainer __css={styles.buttonsContainer('default', 'light')}>
            <Flex
              flexDirection={['column-reverse', null, 'row']}
              alignItems="center"
              justifyContent="center"
              width="100%"
              paddingBottom="10px"
            >
              <SkeletonText
                rows={1}
                skeletonHeight={45}
                width="200px"
                margin={3}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={45}
                width="200px"
                margin={3}
              />
            </Flex>
          </StyledButtonsContainer>
        </StyledContent>
      </StyledContainer>
    </Box>
  );
};

export { Billboard, BillboardSkeleton };
